import React, { useEffect, useRef, useState } from 'react';
import {
  Rocket,
  ArrowRight,
  Info,
  Zap,
  TimerOff,
  CalendarCheck2,
  ShieldCheck,
  Sparkles,
  CircleCheck,
  TrendingUp,
  AlertTriangle,
  Check,
  X,
  ChevronDown,
  Clock3,
  HandHeart,
  WalletCards,
  UserX,
  Ban,
  Star,
  CalendarX,
  RefreshCcw,
  Lock,
  User,
  Smartphone,
  ClipboardList,
  BriefcaseBusiness,
  BarChart3,
  LayoutGrid,
  Bell,
  Mail,
  SquareArrowOutUpRight,
} from 'lucide-react';
import expressCardBg from '../../assets/images/express-card-bg.png';
import { EXPRESS_BOOKING_GIF_URL } from '../../constants/expressBookingAssets';
import { Checkbox } from '../../components/ui/Checkbox';
import { Sheet } from '../../components/ui/Sheet';
import { TermsOfServicePage } from '../legal/TermsOfServicePage';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { EnableExpressBookingDrawer } from '../../components/expressBooking/EnableExpressBookingDrawer';
import { useArtistDetailsStore } from '@/stores/artistDetailsStore';
import apiClient from '../../api/client';
import { API_ENDPOINTS } from '../../api/endpoints';

const highlights = [
  'Unlock more opportunities',
  'Get instant gigs',
  'Boost your visibility',
  'Grow your career in minutes',
];

const stats = [
  { value: '2x', label: 'Faster Bookings' },
  { value: '100%', label: 'Secure Payment' },
  { value: '99%', label: 'Confirmed Bookings' },
];

const expressBenefits = [
  {
    title: 'Lightning Fast',
    description: 'First to accept wins the booking instantly',
    icon: Zap,
  },
  {
    title: 'No waiting',
    description: 'Skip lengthy negotiations and delays',
    icon: TimerOff,
  },
  {
    title: 'Instant Booking',
    description: 'Automatically booked upon acceptance - no paperwork delays',
    icon: CalendarCheck2,
  },
  {
    title: 'Guaranteed Payout',
    description: 'Fixed payout confirmed upfront - no last-minute changes',
    icon: ShieldCheck,
  },
];

const artistAdvantages = [
  {
    title: 'Instant Confirmation',
    description: 'Eliminate lengthy negotiations and secure the gig immediately.',
    icon: CircleCheck,
  },
  {
    title: 'Visibility Boost',
    description:
      'Successfully completing Express Bookings improves your reliability score and leads to higher visibility for future opportunities.',
    icon: TrendingUp,
  },
  {
    title: 'Secured Payout',
    description:
      'The displayed fee is final and confirmed. Funds are processed through secure payments and released as per your membership payout schedule.',
    icon: ShieldCheck,
  },
];

const preAcceptanceChecklist = [
  {
    title: 'Logistics',
    description: 'Check the date, time, and specific venue location.',
  },
  {
    title: 'Feasibility',
    description: 'Confirm travel distance and transportation availability.',
  },
  {
    title: 'Availability',
    description: 'Ensure you’re 100% free with no conflicting holds.',
  },
  {
    title: 'Requirements',
    description: 'Read the dress code, client instructions, and package details carefully.',
  },
  {
    title: 'Payout',
    description: 'Review the fee to ensure it meets your expectations.',
  },
];

const doDonts = {
  dos: [
    {
      title: 'Read Before You Confirm',
      description: 'Understand the brief fully before committing.',
    },
    {
      title: 'Be Prepared',
      description: 'Carry spare equipment to handle technical glitches.',
    },
    {
      title: 'Build Rapport',
      description: 'Treat the host with respect: they are your potential repeat clients.',
    },
    {
      title: 'Focus on Service',
      description: 'Treat every gig as a long-term career opportunity.',
    },
    {
      title: 'Earn Your Tips',
      description: 'Let your performance and attitude drive gratuity, not verbal requests.',
    },
    {
      title: 'Stay Calm',
      description: 'Handle pressure or sudden changes with professional composure.',
    },
    {
      title: 'Time Management',
      description: 'Plan your schedule and arrive prepared for all gigs.',
    },
  ],
  donts: [
    {
      title: 'No Guesswork',
      description: 'Do not accept if you are unsure about your schedule.',
    },
    {
      title: 'No Lateness',
      description: 'Do not arrive late.',
    },
    {
      title: 'Substance Policy',
      description: 'Strictly no alcohol or drug use before, during or after the performance.',
    },
    {
      title: 'No Aggression',
      description: 'Argumentative or abusive language is grounds for immediate ban.',
    },
    {
      title: 'Data Privacy',
      description:
        'Do not exchange personal contact information with the client. Violation results in permanent ban.',
    },
    {
      title: 'No Bypassing',
      description: 'Do not attempt to deal directly with the client for payments.',
    },
    {
      title: 'No Substitutes',
      description: 'You cannot send a replacement artist without prior written approval from StarClinch.',
    },
    {
      title: 'Dress & Hygiene',
      description:
        'No slip-ups, flip-flops, or casual footwear during the event. Maintain proper personal hygiene, clean attire, and a professional appearance at all times.',
    },
  ],
};

const professionalExpectations = [
  { title: 'Punctuality', icon: Clock3, description: 'Arrive 25 minutes prior to the start time to prep/set up for your act.' },
  { title: 'Readiness', icon: CircleCheck, description: 'Bring all required equipment, instruments, gadgets & performance materials, and essential backups (spare cables, batteries, memory cards).' },
  { title: 'Presentation', icon: Sparkles, description: 'Dress strictly according to the event type (formals for corporate, ethnic for weddings, casual for private party).' },
  { title: 'Conduct', icon: HandHeart, description: 'Maintain polite, respectful communication with the client and venue staff.' },
  { title: 'Payment Integrity', icon: WalletCards, description: 'All payments must be processed only through StarClinch.' },
  { title: 'Compliance', icon: ShieldCheck, description: 'Follow venue rules and perform exactly as per the selected package.' },
];

const penaltyTiers = [
  { label: '₹100', description: 'Delays between 5-10 minutes' },
  { label: '₹300', description: 'Delays between 10-20 minutes' },
  { label: '₹500', description: 'Delays up to 30 minutes' },
  { label: 'Higher penalty/Operational review', description: 'Delays beyond 30 minutes' },
];

const noShowCancellationPolicy = [
  {
    title: 'No-Shows',
    description: 'Failing to appear after accepting a booking may lead to permanent account suspension.',
    icon: UserX,
  },
  {
    title: 'Lateness',
    description: 'Repeated delays can reduce your profile visibility.',
    icon: TimerOff,
  },
  {
    title: 'Cancellation',
    description: 'Cancelling a confirmed gig is not allowed.',
    icon: X,
  },
];

const safetyGuidelines = [
  {
    title: 'Zero Tolerance',
    description: 'We do not tolerate abuse, intoxication, or harassment from clients.',
    icon: Ban,
  },
  {
    title: 'Support',
    description:
      'StarClinch will support all genuine, documented safety concerns, involving authorities if necessary.',
    icon: HandHeart,
  },
  {
    title: 'Right to Safety',
    description:
      'If you face an immediate physical threat or harassment, you may leave the venue. If the situation escalates or goes out of hand, please contact your local authorities for assistance.',
    icon: ShieldCheck,
  },
];

const bookingPolicyAccordions = [
  {
    title: 'Ratings & Visibility',
    icon: Star,
    description: (
      <>
        <p className="text-sm leading-relaxed text-white/70">
          Your access to Express Booking is a privilege, not a right. It depends on:
        </p>
        <ul className="mt-2 list-disc pl-5 text-sm leading-relaxed text-white/60">
          <li>100% Punctuality</li>
          <li>Consistent 5-star performance</li>
          <li>Positive client reviews</li>
          <li>Professional on-site behavior</li>
        </ul>
        <p className="mt-3 text-sm font-semibold text-[#F33363] underline decoration-[#F33363] underline-offset-4">
          Your StarClinch profile is your digital reputation - protect it.
        </p>
      </>
    ),
  },
  {
    title: 'Force Majeure (Event Cancellations)',
    icon: CalendarX,
    description: (
      <p className="text-sm leading-relaxed text-white/70">
        StarClinch bears no responsibility for lost income or compensation if an event is cancelled due to
        uncontrollable circumstances such as:
        <ul className="mt-2 list-disc pl-5 text-sm leading-relaxed text-white/60">
          <li>Severe weather</li>
          <li>Government restrictions</li>
          <li>Emergencies or natural disasters</li>
        </ul>
      </p>
    ),
  },
  {
    title: 'Policy Updates',
    icon: RefreshCcw,
    description: (
      <p className="text-sm leading-relaxed text-white/70">
        Express Booking is an evolving feature. Policies may be updated at any time. Continued use of the
        feature constitutes acceptance of the latest terms and conditions.
      </p>
    ),
  },
];

export const ExpressBookingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const artistDetails = useArtistDetailsStore((state) => state.data);
  const ensureArtistDetailsFetched = useArtistDetailsStore((state) => state.ensureFetched);
  const [gstinAcknowledged, setGstinAcknowledged] = useState(false);
  const [termsAcknowledged, setTermsAcknowledged] = useState(false);
  const [termsSheetOpen, setTermsSheetOpen] = useState(false);
  const [privacySheetOpen, setPrivacySheetOpen] = useState(false);
  const [privacyIntroExpanded, setPrivacyIntroExpanded] = useState(false);
  const [expandedPrivacyCardId, setExpandedPrivacyCardId] = useState<number | null>(null);
  const [enableDrawerOpen, setEnableDrawerOpen] = useState(false);
  const [hasRejectedLocation, setHasRejectedLocation] = useState(false);
  const [isRejectedLocationResolved, setIsRejectedLocationResolved] = useState(false);
  // Single-open accordion behavior across this page section.
  const [openAccordionKey, setOpenAccordionKey] = useState<string | null>(null);
  // DOs & DON&rsquo;Ts expand/collapse (show only first 3 initially).
  const [dosExpanded, setDosExpanded] = useState(false);
  const [dontsExpanded, setDontsExpanded] = useState(false);

  // Route-driven behavior:
  // - '/express-booking' => open enable drawer by default
  // - '/express-booking-consent-form' => keep drawer closed
  useEffect(() => {
    const pathname = location.pathname;
    const isEnablePage = pathname === '/express-booking';

    if (isEnablePage) setEnableDrawerOpen(true);
    else setEnableDrawerOpen(false);
  }, [location.pathname]);

  const refreshRejectedLocationFlag = async () => {
    try {
      const res = await apiClient.get(API_ENDPOINTS.EXPRESS_BOOKING.BASE);
      const data = res?.data?.data;
      setHasRejectedLocation(data?.has_rejected_location === true);
    } catch {
      // Fall back to approved UI if fetch fails.
    } finally {
      setIsRejectedLocationResolved(true);
    }
  };

  useEffect(() => {
    void refreshRejectedLocationFlag();
  }, []);

  const abideSectionRef = useRef<HTMLDivElement | null>(null);

  const handleAcceptNowScroll = () => {
    const el = abideSectionRef.current;
    if (!el) return;

    // Scroll within the nearest overflow-y container (UserHome uses its own scrolling main element).
    // Add a small extra offset so the CTA doesn't land "slightly above" the bottom nav area.
    const findScrollableParent = (node: HTMLElement | null): HTMLElement | null => {
      let cur: HTMLElement | null = node;
      while (cur) {
        const style = window.getComputedStyle(cur);
        const overflowY = style.overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') return cur;
        cur = cur.parentElement;
      }
      return null;
    };

    const container = findScrollableParent(el) ?? document.scrollingElement;

    const elRect = el.getBoundingClientRect();
    const containerRect =
      container && container instanceof HTMLElement
        ? container.getBoundingClientRect()
        : { top: 0, left: 0, width: 0, height: 0 } as DOMRect;

    const currentScrollTop =
      container && container instanceof HTMLElement
        ? container.scrollTop
        : window.scrollY;

    const offsetPx = 160; // accounts for doubled bottom nav + padding
    const delta = elRect.top - containerRect.top;
    const targetTop = Math.max(0, currentScrollTop + delta + offsetPx);

    if (container && container instanceof HTMLElement) {
      container.scrollTo({ top: targetTop, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: targetTop, behavior: 'smooth' });
    }
  };

  const canAcknowledge = gstinAcknowledged && termsAcknowledged;
  const isExpressBookingApproved =
    artistDetails?.eb_tnc === true &&
    (artistDetails?.eb_enabled === 1 ||
      artistDetails?.eb_enabled === 2 ||
      String(artistDetails?.eb_enabled_label ?? '').trim().toLowerCase() === 'approved');
  const showApprovedState =
    isRejectedLocationResolved && isExpressBookingApproved && !hasRejectedLocation;

  const renderCtaSkeleton = (className = '') => (
    <div
      className={`${className} inline-flex h-[52px] w-[200px] animate-pulse rounded-3xl bg-white/10 sm:h-[60px] sm:w-[220px] lg:h-[72px] lg:w-[240px]`}
      aria-hidden
    />
  );

  const renderExpressBookingCta = (className = '') => {
    if (isExpressBookingApproved && !isRejectedLocationResolved) {
      return renderCtaSkeleton(className);
    }

    if (showApprovedState) {
      return (
        <button
          type="button"
          disabled
          className={`${className} inline-flex items-center gap-2 rounded-3xl border border-emerald-400/30 bg-emerald-500/15 px-7 py-2.5 text-xl font-bold text-emerald-100 shadow-[0_12px_30px_rgba(16,185,129,0.22)] sm:px-9 sm:py-3.5 sm:text-2xl lg:px-10 lg:py-4 lg:text-3xl`}
        >
          <Check className="h-5 w-5 text-emerald-300" />
          Approved
        </button>
      );
    }

    return (
      <button
        type="button"
        onClick={handleAcceptNowScroll}
        className={`${className} inline-flex rounded-3xl bg-[#F33363] px-7 py-2.5 text-xl font-bold text-white shadow-[0_12px_30px_rgba(243,51,99,0.45)] transition-transform hover:scale-[1.02] sm:px-9 sm:py-3.5 sm:text-2xl lg:px-10 lg:py-4 lg:text-3xl`}
      >
        Accept Now
      </button>
    );
  };

  const renderAcknowledgementCta = (className = '') => {
    if (isExpressBookingApproved && !isRejectedLocationResolved) {
      return renderCtaSkeleton(className);
    }

    if (showApprovedState) {
      return (
        <button
          type="button"
          disabled
          className={`${className} inline-flex items-center gap-2 rounded-3xl border border-emerald-400/30 bg-emerald-500/15 px-7 py-2.5 text-xl font-bold text-emerald-100 shadow-[0_12px_30px_rgba(16,185,129,0.22)] sm:px-9 sm:py-3.5 sm:text-2xl lg:px-10 lg:py-4 lg:text-3xl`}
        >
          <Check className="h-5 w-5 text-emerald-300" />
          Approved
        </button>
      );
    }

    return (
      <button
        type="button"
        onClick={handleAcknowledge}
        className={`${className} inline-flex rounded-3xl bg-[#F33363] px-7 py-2.5 text-xl font-bold text-white shadow-[0_12px_30px_rgba(243,51,99,0.45)] transition-transform hover:scale-[1.02] sm:px-9 sm:py-3.5 sm:text-2xl lg:px-10 lg:py-4 lg:text-3xl`}
      >
        I Shall Abide
      </button>
    );
  };

  const privacyItems = [
    {
      n: 1,
      title: 'Information We Collect',
      expanded: (
        <>
          <p className="text-sm leading-relaxed text-white/60">
            When you use the StarClinch Website and App, we collect different types of information.
          </p>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
              <User className="h-5 w-5 text-[#F33363]" />
              <div className="mt-3 text-sm font-semibold text-white">Personal Information</div>
              <div className="mt-1 text-xs leading-relaxed text-white/60">
                When you use the StarClinch Website and App, we collect information you provide to
                us. To log in or register for the App, we collect information such as your Facebook
                ID, name, email address, profile picture, and other publicly available Facebook profile
                data.
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
              <Smartphone className="h-5 w-5 text-[#F33363]" />
              <div className="mt-3 text-sm font-semibold text-white">
                Automatically Collected Information
              </div>
              <div className="mt-1 text-xs leading-relaxed text-white/60">
                We may collect device information, IP address, browser type, and other technical data
                when you access our platforms.
              </div>
            </div>
          </div>

          <div className="mt-3 rounded-2xl border border-white/10 bg-black/20 p-4">
            <ClipboardList className="h-5 w-5 text-[#F33363]" />
            <div className="mt-3 text-sm font-semibold text-white">Express Booking Data</div>
            <div className="mt-1 text-xs leading-relaxed text-white/60">
              We collect specific data related to Express Booking usage, including response times,
              attendance records, performance ratings, client feedback, and real-time location (used
              for attendance verification, safety confirmation, and dispute resolution).
            </div>
          </div>
        </>
      ),
    },
    {
      n: 2,
      title: 'Use of Information',
      expanded: (
        <>
          <p className="text-sm leading-relaxed text-white/60">
            We use the information we collect for the following purposes:
          </p>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
              <BriefcaseBusiness className="h-5 w-5 text-[#F33363]" />
              <div className="mt-3 text-sm font-semibold text-white">Providing Services</div>
              <div className="mt-1 text-xs leading-relaxed text-white/60">
                The StarClinch Website and App enable artists to create profiles, get discovered, and
                be booked by clients. We maintain artist profiles on both platforms to promote talent.
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
              <BarChart3 className="h-5 w-5 text-[#F33363]" />
              <div className="mt-3 text-sm font-semibold text-white">Analytics and Improvement</div>
              <div className="mt-1 text-xs leading-relaxed text-white/60">
                We use Google Analytics to analyze Website traffic and improve our services.
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
              <LayoutGrid className="h-5 w-5 text-[#F33363]" />
              <div className="mt-3 text-sm font-semibold text-white">Content Display</div>
              <div className="mt-1 text-xs leading-relaxed text-white/60">
                Images and videos imported to our platforms, if approved by our internal team, may
                be displayed on your profile.
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
              <UserX className="h-5 w-5 text-[#F33363]" />
              <div className="mt-3 text-sm font-semibold text-white">Account Deletion</div>
              <div className="mt-1 text-xs leading-relaxed text-white/60">
                Your data will be deleted when you delete your account, though some information may
                be retained for legal and compliance purposes.
              </div>
            </div>
          </div>

          <div className="mt-3 rounded-2xl border border-white/10 bg-black/20 p-4">
            <Sparkles className="h-5 w-5 text-[#F33363]" />
            <div className="mt-3 text-sm font-semibold text-white">Feature Optimization</div>
            <div className="mt-1 text-xs leading-relaxed text-white/60">
              Data collected via Express Booking is strictly used to calculate reliability scores,
              ensure artist safety, and determine fair and data-driven visibility rankings within the
              booking algorithm.
            </div>
          </div>
        </>
      ),
    },
    {
      n: 3,
      title: 'Communications',
      expanded: (
        <>
          <p className="text-sm leading-relaxed text-white/60">
            We use the information we collect for the following purposes:
          </p>

          <div className="mt-4 rounded-2xl border border-white/10 bg-black/20 p-4">
            <Bell className="h-5 w-5 text-[#F33363]" />
            <div className="mt-3 text-sm font-semibold text-white">Operational Alerts</div>
            <div className="mt-1 text-xs leading-relaxed text-white/60">
              Users of Express Booking consent to receive mandatory, time-sensitive operational
              notifications via SMS, Email, WhatsApp, or Push Notifications necessary for the timely
              fulfillment of the contracted gig.
            </div>
          </div>
        </>
      ),
    },
    {
      n: 4,
      title: 'Data Deletion Instructions',
      expanded: (
        <>
          <p className="text-sm leading-relaxed text-white/60">
            If you want to delete your data that you have imported from Instagram, follow these
            steps:
          </p>

          <div className="mt-4 space-y-3">
            {[
              'Go to your Instagram Account’s “Settings & Privacy”.',
              'Click “Settings”.',
              'Then click “Website permissions”.',
              'Look for “Apps and Websites” and you will see all the apps and websites you have linked with your Instagram account.',
              'Search and click “Artist Dashboard”.',
              'Scroll and click “Remove”.',
            ].map((text, idx) => (
              <div key={text} className="flex gap-3">
                <div className="w-5 text-sm font-semibold text-[#F33363]">{idx + 1}</div>
                <div className="text-xs leading-relaxed text-white/60">{text}</div>
              </div>
            ))}
          </div>
        </>
      ),
    },
    {
      n: 5,
      title: 'Contact Information',
      expanded: (
        <>
          <p className="text-sm leading-relaxed text-white/60">
            If you have any questions or concerns regarding this Privacy Policy ..
          </p>

          <div className="mt-4 rounded-2xl border border-white/10 bg-black/20 p-4">
            <Mail className="h-5 w-5 text-[#F33363]" />
            <div className="mt-3 text-sm font-semibold text-white">
              Please contact Ms. Shambhavi Tiwari at
            </div>
            <div className="mt-1 flex items-center gap-2 text-sm text-white/70">
              <span>connect@starclinch.com</span>
              <SquareArrowOutUpRight className="h-4 w-4 text-[#F33363]" />
            </div>
            <div className="mt-3 text-xs leading-relaxed text-white/60">
              StarClinch® is a registered trademark.
            </div>
          </div>
        </>
      ),
    },
  ];

  const handleAcknowledge = async () => {
    if (!canAcknowledge) {
      toast.error('Please check both confirmations to continue.');
      return;
    }

    const details = artistDetails ?? (await ensureArtistDetailsFetched());
    const fullProfile = details?.user_info?.profile_completion?.full_profile ?? 0;
    if (fullProfile < 100) {
      toast.error('First, Complete the profile');
      navigate('/profile');
      return;
    }

    const status = (details?.user_info?.profile_status ?? '').trim().toLowerCase();
    const isLiveStatus = status === 'live' || status === 'approved';
    if (!isLiveStatus) {
      toast.error('Profile is not live yet');
      return;
    }

    toast.success('Acknowledgement saved.');
    // Open the enable drawer on the same page instead of navigating away.
    // The route-driven effect keeps it closed on other paths, so we only toggle state here.
    setEnableDrawerOpen(true);
  };

  const digitalAcknowledgementItems = [
    {
      id: 'gstin',
      label: 'I/We confirm that we do not have a GSTIN.',
    },
    {
      id: 'terms',
      label: (
        <>
          I have read, understood, and agree to the Express Booking Terms and Guidelines, the{' '}
          <a
            href="#"
            className="text-[#F33363] underline decoration-[#F33363] underline-offset-4"
            onClick={(e) => {
              e.preventDefault();
              setPrivacySheetOpen(true);
            }}
          >
            StarClinch Privacy Policy
          </a>{' '}
          and{' '}
          <a
            href="#"
            className="text-[#F33363] underline decoration-[#F33363] underline-offset-4"
            onClick={(e) => {
              e.preventDefault();
              setTermsSheetOpen(true);
            }}
          >
            StarClinch Terms of Use
          </a>
          .
        </>
      ),
    },
  ];

  return (
    <div className="w-full  py-10 pb-24 text-white">
      <div className="mx-auto w-full">
        <section
          className="relative overflow-hidden rounded-[2rem] border border-white/10 p-5 sm:p-8 md:p-10 lg:p-12 "
          style={{
            backgroundImage: `url(${expressCardBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="flex flex-col items-center justify-center">
            <h1 className="text-3xl font-black leading-tight sm:text-4xl lg:text-6xl text-center">
              Welcome to
              <br />
              <span className="text-white/50">Express Booking</span>
            </h1>
            <div className="mt-5 inline-flex items-center gap-2 w-fit rounded-full border border-[#F33363]/35 bg-[#4B0017]/60 px-4 py-2 text-sm font-semibold text-[#F6C5D3] shadow-[0_0_32px_rgba(243,51,99,0.25)] sm:px-5 sm:py-2.5 sm:text-base lg:text-lg">
              <Rocket className="h-4 w-4 text-[#F33363] sm:h-5 sm:w-5" />
              <span>Fast-Track Your Career</span>
            </div>

          </div>
          {/* <div className="absolute inset-0 bg-black/55" /> */}

          <div className="relative z-10 grid grid-cols-2 md:gap-8 lg:grid-cols-[1.15fr_0.85fr] items-center lg:gap-10">
            <div>
              <ul className="mt-7 space-y-2.5 sm:space-y-3">
                {highlights.map((item) => (
                  <li key={item} className="flex items-center gap-2.5 text-sm text-white/90 sm:gap-3 sm:text-base lg:text-lg">
                    <ArrowRight className="h-4 w-4 text-white/80 sm:h-5 sm:w-5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>


            </div>


            <img
              src={EXPRESS_BOOKING_GIF_URL}
              alt="Express booking in action"
              className="h-auto w-full max-w-[180px] sm:max-w-[380px] lg:max-w-[420px] object-contain"
            />

          </div>
          <div className="flex flex-col items-center justify-center">
            <div className="mt-8 grid grid-cols-3 gap-4 text-center  sm:gap-3">
              {stats.map((item) => (
                <div key={item.label} className="rounded-2xl  px-4 py-3">
                  <p className="text-3xl font-black sm:text-4xl">{item.value}</p>
                  <p className="mt-1 text-sm text-white/70 sm:text-base lg:text-lg">{item.label}</p>
                </div>
              ))}
            </div>

            {renderExpressBookingCta('mt-4')}
          </div>

        </section>

        <section className="mt-6 sm:mt-8 rounded-[2rem]   p-5 sm:p-8 md:p-10 lg:p-12">
          <div className="">
            <div className="flex flex-col items-center text-center">
              <h2 className="text-3xl font-black leading-tight sm:text-4xl lg:text-6xl">
                What is
                <br />
                <span className="text-white/50">Express Booking?</span>
              </h2>
              <div className="mt-5 inline-flex items-center gap-2 w-fit rounded-full border border-[#F33363]/35 bg-[#4B0017]/60 px-4 py-2 text-sm font-semibold text-[#F6C5D3] shadow-[0_0_32px_rgba(243,51,99,0.25)] sm:px-5 sm:py-2.5 sm:text-base lg:text-lg">
                <Info className="h-4 w-4 text-[#F33363] sm:h-5 sm:w-5" />
                <span>How it works</span>
              </div>

              <p className="mt-6 max-w-4xl text-base leading-relaxed text-white/65 sm:text-base lg:text-lg lg:leading-[1.35]">
                Your exclusive pathway to
                <span className="text-white"> rapid, confirmed gigs.</span> A swift-action feature: the
                <span className="text-[#F33363]"> first artist to review and accept</span> the offer is instantly booked for the event.
              </p>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-2 xl:grid-cols-4 sm:gap-5 lg:mt-10 lg:gap-6">
              {expressBenefits.map((item) => (
                <article
                  key={item.title}
                  className="rounded-[1.75rem] border border-white/10 bg-[radial-gradient(120%_120%_at_100%_100%,rgba(255,255,255,0.2)_0%,rgba(14,14,16,0.9)_48%,rgba(8,8,10,0.95)_100%)] p-3 sm:p-5 lg:p-7"
                >
                  <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[radial-gradient(circle_at_40%_40%,rgba(243,51,99,0.45),rgba(75,0,23,0.6)_65%)] sm:h-14 sm:w-14">
                    <item.icon className="h-5 w-5 text-[#F33363] sm:h-6 sm:w-6" />
                  </div>
                  <h3 className="text-xl font-semibold text-white sm:text-lg lg:text-2xl">{item.title}</h3>
                  <p className="mt-2 leading-snug text-white/65 text-sm sm:text-base lg:text-lg">{item.description}</p>
                </article>
              ))}
            </div>

            <p className="mx-auto mt-8 max-w-4xl text-center text-base text-white/45 ssm:text-base lg:text-lg lg:mt-10 ">
              This streamlined process is designed for professionals ready to mobilize and perform immediately.
            </p>
          </div>
        </section>

        <section className="mt-6 sm:mt-8 rounded-[2rem]  p-5 sm:p-8 md:p-10 lg:p-12">
          <div className="">
            <div className="flex flex-col items-center text-center">
              <h2 className="text-3xl font-black leading-tight sm:text-4xl lg:text-6xl">
                The Artist
                <br />
                <span className="text-white/50">Advantage</span>
              </h2>
              <div className="mt-5 inline-flex items-center gap-2 w-fit rounded-full border border-[#F33363]/35 bg-[#4B0017]/60 px-4 py-2 text-sm font-semibold text-[#F6C5D3] shadow-[0_0_32px_rgba(243,51,99,0.25)] sm:px-5 sm:py-2.5 sm:text-base lg:text-lg">
                <Sparkles className="h-4 w-4 text-[#F33363] sm:h-5 sm:w-5" />
                <span>Why Choose Express Booking?</span>
              </div>
              <p className="mt-6 max-w-4xl leading-relaxed text-white/85 text-sm sm:text-base lg:text-lg lg:leading-[1.35]">
                Express Booking provides significant value to active artists by offering:
              </p>
            </div>

            <div className="relative mt-8 space-y-6 sm:space-y-7 lg:mt-10 lg:space-y-8">
              <div className="pointer-events-none absolute left-6 top-6 hidden h-[calc(100%-2.5rem)] w-px bg-gradient-to-b from-[#F33363]/50 via-[#F33363]/35 to-[#F33363]/10 sm:block" />
              {artistAdvantages.map((item) => (
                <article
                  key={item.title}
                  className="relative rounded-[1.5rem] border border-white/10 bg-[linear-gradient(135deg,rgba(8,8,10,0.95),rgba(18,18,22,0.85))] p-4 sm:pl-16 sm:pr-6 sm:py-6 lg:p-7 lg:pl-20"
                >
                  <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[radial-gradient(circle_at_40%_40%,rgba(243,51,99,0.45),rgba(75,0,23,0.6)_65%)] sm:absolute sm:left-3.5 sm:top-6 sm:mb-0 sm:h-10 sm:w-10 lg:left-5 lg:top-7 lg:h-11 lg:w-11">
                    <item.icon className="h-5 w-5 text-[#F33363]" />
                  </div>
                  <h3 className="text-xl font-semibold text-white sm:text-lg lg:text-2xl">{item.title}</h3>
                  <p className="mt-2 leading-snug text-white/65 text-sm sm:text-base lg:text-lg">
                    {item.description}
                  </p>
                </article>
              ))}
            </div>

            <div className="mt-8 flex justify-center lg:mt-10">
              {renderExpressBookingCta()}
            </div>
          </div>
        </section>

        <section className="mt-6 sm:mt-8 rounded-[2rem] border border-white/10 bg-gradient-to-b from-[#07070A] via-[#06060A] to-[#050506] p-5 sm:p-8 md:p-10 lg:p-12">
          <div className="">
            <div className="flex flex-col items-center text-center">
              <h2 className="text-3xl font-black leading-tight sm:text-4xl lg:text-6xl">
                Pre-Acceptance
                <br />
                <span className="text-white/50">Checklist</span>
              </h2>
            </div>

            <div className="relative mt-8 grid grid-cols-1 gap-4 sm:gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:mt-10">
              {preAcceptanceChecklist.map((item) => (
                <article
                  key={item.title}
                  className="rounded-[1.5rem] border border-white/10 bg-[linear-gradient(135deg,rgba(8,8,10,0.95),rgba(18,18,22,0.85))] p-5 sm:p-6 lg:p-7"
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full bg-[radial-gradient(circle_at_40%_40%,rgba(243,51,99,0.45),rgba(75,0,23,0.6)_65%)]">
                      <CircleCheck className="h-5 w-5 text-[#F33363]" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white sm:text-lg lg:text-2xl">
                        {item.title}
                      </h3>
                      <p className="mt-2 leading-snug text-white/65 text-sm sm:text-base lg:text-lg">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div className="mt-6 rounded-[1.5rem] flex flex-row items-center justify-center">
              <div className="flex items-start gap-3">
                <AlertTriangle className=" h-6 w-6 text-yellow-500" />
                <p className="text-sm leading-relaxed text-white/75 sm:text-base lg:text-lg lg:leading-[1.35]">
                  <span className="font-semibold text-[#F6C5D3]">Note:</span>{' '}
                  <span className="text-yellow-500 font-semibold">
                    Cancellation after acceptance is strictly prohibited.
                  </span>
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="mt-20 rounded-[2rem] ">
          <div className="">
            <div className="flex flex-col items-center text-center">
              <h2 className="text-3xl font-black leading-tight sm:text-4xl lg:text-6xl">
                DOs &amp; DON&rsquo;Ts
                <br />
                <span className="text-white/50">for a Successful Gig</span>
              </h2>
            </div>

            <div className="relative mt-8 grid grid-cols-1 gap-4 sm:gap-5 lg:grid-cols-2 lg:mt-10">
              {/* DOs */}
              <article className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-[radial-gradient(120%_120%_at_0%_0%,rgba(0,255,120,0.20)_0%,rgba(10,10,12,0.92)_55%,rgba(0,0,0,0.75)_100%)] p-5 sm:p-6 lg:p-7">
                <div className="absolute left-5 top-5 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[radial-gradient(circle_at_40%_40%,rgba(0,255,120,0.35),rgba(0,80,40,0.65)_65%)]">
                  <Check className="h-5 w-5 text-[#00ff78]" />
                </div>
                <div
                  className={`pt-14 space-y-5 overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out ${
                    dosExpanded ? 'max-h-[520px] opacity-100' : 'max-h-[260px] opacity-100'
                  }`}
                >
                  {(dosExpanded ? doDonts.dos : doDonts.dos.slice(0, 3)).map((item) => (
                    <div key={item.title}>
                      <h3 className="text-xl font-semibold text-white sm:text-lg lg:text-2xl">
                        {item.title}
                      </h3>
                      <p className="mt-1 leading-relaxed text-white/50 text-sm sm:text-base lg:text-lg">
                        {item.description}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="mt-7 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setDosExpanded((v) => !v)}
                    aria-expanded={dosExpanded}
                    className="inline-flex items-center justify-center"
                  >
                    <ChevronDown
                      className={`h-5 w-5 text-white/70 transition-transform duration-300 ease-in-out ${dosExpanded ? 'rotate-180' : ''
                        }`}
                    />
                  </button>
                </div>
              </article>

              {/* DON&rsquo;Ts */}
              <article className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-[radial-gradient(120%_120%_at_100%_0%,rgba(243,51,99,0.22)_0%,rgba(10,10,12,0.92)_55%,rgba(0,0,0,0.75)_100%)] p-5 sm:p-6 lg:p-7">
                <div className="absolute left-5 top-5 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[radial-gradient(circle_at_40%_40%,rgba(243,51,99,0.35),rgba(115,0,30,0.65)_65%)]">
                  <X className="h-5 w-5 text-[#F33363]" />
                </div>
                <div
                  className={`pt-14 space-y-5 overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out ${
                    dontsExpanded ? 'max-h-[700px] opacity-100' : 'max-h-[260px] opacity-100'
                  }`}
                >
                  {(dontsExpanded ? doDonts.donts : doDonts.donts.slice(0, 3)).map(
                    (item) => (
                      <div key={item.title}>
                        <h3 className="text-xl font-semibold text-white sm:text-lg lg:text-2xl">
                          {item.title}
                        </h3>
                        <p className="mt-1 leading-relaxed text-white/50 text-sm sm:text-base lg:text-lg">
                          {item.description}
                        </p>
                      </div>
                    ),
                  )}
                </div>

                <div className="mt-7 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setDontsExpanded((v) => !v)}
                    aria-expanded={dontsExpanded}
                    className="inline-flex items-center justify-center"
                  >
                    <ChevronDown
                      className={`h-5 w-5 text-white/70 transition-transform duration-300 ease-in-out ${dontsExpanded ? 'rotate-180' : ''
                        }`}
                    />
                  </button>
                </div>
              </article>
            </div>

            <div className="mt-8 flex justify-center lg:mt-10">
              {renderExpressBookingCta()}
            </div>
          </div>
        </section>

        <section className="mt-20 rounded-[2rem] ">
          <div className="">
            <div className="flex flex-col items-center text-center">
              <h2 className="text-3xl font-black leading-tight sm:text-4xl lg:text-6xl">
                Professional
                <br />
                <span className="text-white/50">Expectations</span>
              </h2>
              <p className="mt-4 max-w-4xl text-center text-sm text-white/45 sm:text-base lg:text-lg lg:leading-[1.35]">
                By using Express Booking, you agree to the above standards.
              </p>
            </div>

            <div className="mt-8 space-y-4 sm:space-y-5 lg:mt-10">
              {professionalExpectations.map((item) => {
                const Icon = item.icon;
                const key = `professional:${item.title}`;
                const isOpen = openAccordionKey === key;
                return (
                  <div
                    key={item.title}
                    className="rounded-[1.5rem] border border-white/10 bg-[linear-gradient(135deg,rgba(8,8,10,0.95),rgba(18,18,22,0.85))] px-4 py-5 sm:px-6"
                  >
                    <button
                      type="button"
                      className="w-full flex items-center justify-between text-left"
                      onClick={() => {
                        setOpenAccordionKey((prev) => (prev === key ? null : key));
                      }}
                      aria-expanded={isOpen}
                    >
                      <div className="flex items-center gap-3">
                        <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#4B0017]/60 border border-[#F33363]/35">
                          <Icon className="h-5 w-5 text-[#F33363]" />
                        </div>
                        <h3 className="text-lg font-semibold text-white sm:text-xl lg:text-2xl">
                          {item.title}
                        </h3>
                      </div>
                      <ChevronDown
                        className={`h-5 w-5 text-white/60 transition-transform duration-300 ease-in-out ${isOpen ? 'rotate-180' : ''
                          }`}
                      />
                    </button>

                    <div
                      className={`overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out ${isOpen ? 'max-h-[220px] opacity-100' : 'max-h-0 opacity-0'
                        }`}
                    >
                      <div className={isOpen ? 'pt-3' : 'pt-0'}>
                        <p className="text-sm leading-relaxed text-white/65 sm:text-base lg:text-lg">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="mt-6 sm:mt-8 rounded-[2rem] border border-white/10 bg-gradient-to-b from-[#07070A] via-[#06060A] to-[#050506] p-5 sm:p-8 md:p-10 lg:p-12">
          <div className="mx-auto max-w-5xl">
            <div className="flex flex-col items-center text-center">
              <h2 className="text-3xl font-black leading-tight sm:text-4xl lg:text-6xl">
                No-Show &amp; Cancellation
                <br />
                Policy
              </h2>

              <p className="mt-4 max-w-4xl text-sm leading-relaxed text-white/45 sm:text-base lg:text-lg lg:leading-[1.35]">
                Understand our policy before accepting a booking. Repeated violations may affect your account status.
              </p>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-3 sm:gap-4 sm:mt-10">
              {noShowCancellationPolicy.map((item) => {
                const Icon = item.icon;
                return (
                  <article key={item.title} className="text-center">
                    <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full border border-[#F33363]/20 bg-[#4B0017]/25">
                      <Icon className="h-5 w-5 text-[#F33363]" />
                    </div>
                    <h3 className="mt-4 text-base font-semibold text-white sm:text-lg lg:text-xl">
                      {item.title}
                    </h3>
                    <p className="mt-2 text-xs leading-relaxed text-white/60 sm:text-sm lg:text-base">
                      {item.description}
                    </p>
                  </article>
                );
              })}
            </div>

            <div className="mt-6">
              <button
                type="button"
                className="w-full flex items-center justify-between"
                onClick={() => {
                  setOpenAccordionKey((prev) => (prev === 'penalties' ? null : 'penalties'));
                }}
                aria-expanded={openAccordionKey === 'penalties'}
              >
                <h3 className="text-base font-semibold text-[#F33363] sm:text-lg lg:text-xl">
                  Penalties
                </h3>
                <ChevronDown
                  className={`h-5 w-5 text-white/60 transition-transform duration-300 ease-in-out ${openAccordionKey === 'penalties' ? 'rotate-180' : ''
                    }`}
                />
              </button>
              <p className="mt-0 text-xs leading-relaxed text-white/70 sm:text-sm lg:text-base lg:leading-[1.35]">
                Repeated delays or consistent operational issues may lead to escalated action.
              </p>

              <div
                className={`mt-3 overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out ${openAccordionKey === 'penalties' ? 'max-h-[260px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
              >
                <div className="grid grid-cols-[140px_1fr] gap-x-5 gap-y-3">
                  {penaltyTiers.map((tier) => (
                    <div key={tier.label} className="contents">
                      <div className="text-sm font-medium text-white/90">
                        {tier.label}
                      </div>
                      <div className="text-sm leading-relaxed text-white/65">
                        {tier.description}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mt-6 sm:mt-8 rounded-[2rem] border border-white/10 bg-gradient-to-b from-[#07070A] via-[#06060A] to-[#050506] p-5 sm:p-8 md:p-10 lg:p-12">
          <div className="mx-auto max-w-5xl">
            <div className="flex flex-col items-center text-center">
              <h2 className="text-3xl font-black leading-tight sm:text-4xl lg:text-6xl">
                Safety
                <br />
                Guidelines
              </h2>
            </div>

            <div className="relative mt-8 space-y-6 sm:space-y-7 lg:mt-10 lg:space-y-8">
              <div className="pointer-events-none absolute left-6 top-6 hidden h-[calc(100%-2.5rem)] w-px bg-gradient-to-b from-[#F33363]/50 via-[#F33363]/35 to-[#F33363]/10 sm:block" />
              {safetyGuidelines.map((item) => {
                const Icon = item.icon;
                return (
                  <article
                    key={item.title}
                    className="relative rounded-[1.5rem] border border-white/10 bg-[linear-gradient(135deg,rgba(8,8,10,0.95),rgba(18,18,22,0.85))] p-4 sm:pl-16 sm:pr-6 sm:py-6 lg:p-7 lg:pl-20"
                  >
                    <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[radial-gradient(circle_at_40%_40%,rgba(243,51,99,0.45),rgba(75,0,23,0.6)_65%)] sm:absolute sm:left-3.5 sm:top-6 sm:mb-0 sm:h-10 sm:w-10 lg:left-5 lg:top-7 lg:h-11 lg:w-11">
                      <Icon className="h-5 w-5 text-[#F33363]" />
                    </div>
                    <h3 className="text-xl font-semibold text-white sm:text-lg lg:text-2xl">
                      {item.title}
                    </h3>
                    <p className="mt-2 leading-snug text-white/65 text-sm sm:text-base lg:text-lg">
                      {item.description}
                    </p>
                  </article>
                );
              })}
            </div>

            <div className="mt-10 flex justify-center">
              {renderExpressBookingCta()}
            </div>
          </div>
        </section>

        <section className="mt-6 sm:mt-8 rounded-[2rem] ">
          <div className="mx-auto max-w-5xl">
            <div className="space-y-4 sm:space-y-5">
              {bookingPolicyAccordions.map((item) => {
                const Icon = item.icon;
                const key = `policy:${item.title}`;
                const isOpen = openAccordionKey === key;
                return (
                  <div
                    key={item.title}
                    className="rounded-[1.5rem] border border-white/10 bg-[linear-gradient(135deg,rgba(255,255,255,0.06),rgba(10,10,12,0.35))]"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenAccordionKey((prev) => (prev === key ? null : key))}
                      className="w-full flex items-center justify-between px-4 py-4 sm:px-5"
                      aria-expanded={isOpen}
                    >
                      <div className="flex items-center gap-4">
                        <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#4B0017]/55 border border-[#F33363]/25">
                          <Icon className="h-5 w-5 text-[#F33363]" />
                        </div>
                        <h3 className="text-base font-semibold text-white sm:text-lg">
                          {item.title}
                        </h3>
                      </div>
                      <ChevronDown
                        className={`h-5 w-5 text-white/60 transition-transform duration-300 ease-in-out ${isOpen ? 'rotate-180' : ''
                          }`}
                      />
                    </button>

                    <div
                      className={`overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out ${isOpen ? 'max-h-[240px] opacity-100' : 'max-h-0 opacity-0'
                        }`}
                    >
                      <div className="px-4 pb-4 sm:px-5">{item.description}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="mt-6 sm:mt-8 rounded-[2rem] ">
          <div className="mx-auto max-w-5xl">
            <div className="flex flex-col items-center text-center">
              <h2 className="text-3xl font-black leading-tight sm:text-4xl lg:text-6xl">
                Digital
                <br />
                Acknowledgement
              </h2>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/70 sm:text-base lg:text-lg lg:leading-[1.35]">
                Please confirm you have read and agree to all terms and related policies:
              </p>
            </div>

            <div className="mt-6 rounded-[1.5rem] border border-white/10 bg-[linear-gradient(135deg,rgba(255,255,255,0.06),rgba(10,10,12,0.35))] p-4 sm:p-6">
              <div className="space-y-4">
                {digitalAcknowledgementItems.map((item) => (
                  <div key={item.id} className="flex items-start gap-3">
                    <Checkbox
                      checked={item.id === 'gstin' ? gstinAcknowledged : termsAcknowledged}
                      onCheckedChange={(v) => {
                        if (item.id === 'gstin') setGstinAcknowledged(v);
                        else setTermsAcknowledged(v);
                      }}
                      className="mt-1"
                    />
                    <span className="text-sm leading-relaxed text-white/80 sm:text-base">
                      <span
                        role="button"
                        tabIndex={0}
                        className="cursor-pointer"
                        onClick={(e) => {
                          const target = e.target as HTMLElement;
                          if (target.closest('a')) return;

                          if (item.id === 'gstin') setGstinAcknowledged((p) => !p);
                          else setTermsAcknowledged((p) => !p);
                        }}
                      >
                        {item.label}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div ref={abideSectionRef} className="mt-8 flex justify-center">
              {renderAcknowledgementCta()}
            </div>
          </div>
        </section>

        <Sheet open={termsSheetOpen} onOpenChange={setTermsSheetOpen} >
          <div className="w-full">
            <TermsOfServicePage inSheet />
          </div>
        </Sheet>

        <Sheet
          open={privacySheetOpen}
          onOpenChange={(open) => {
            setPrivacySheetOpen(open);
            if (!open) {
              setPrivacyIntroExpanded(false);
              setExpandedPrivacyCardId(null);
            }
          }}

        >
          <div className="w-full">
            <div className="flex flex-col items-center text-center">
              <div className="mt-2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#4B0017]/60 border border-[#F33363]/35">
                <Lock className="h-5 w-5 text-[#F33363]" />
              </div>

              <h2 className="mt-4 text-xl font-bold text-white sm:text-2xl">
                Privacy Policy
              </h2>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-white/60 sm:text-base">
                Your privacy is important to us. This policy explains how we collect,
                use, and protect your personal information.
              </p>

              <a
                href="#"
              onClick={(e) => {
                e.preventDefault();
                setPrivacyIntroExpanded((v) => !v);
              }}
                className="mt-3 text-sm font-semibold text-[#3B82F6] underline underline-offset-4"
              >
              {privacyIntroExpanded ? 'Read less' : 'Read more'}
              </a>
            </div>

            {privacyIntroExpanded && (
              <div className="mt-3 rounded-2xl border border-white/10 bg-[#1C1C1E] px-4 py-3">
                <p className="text-sm leading-relaxed text-white/60">
                  The website artist.starclinch.com (the &quot;Website&quot;) and the mobile application
                  StarClinch Artist Cockpit (the &quot;App&quot;) are developed, owned, and operated by VINSM
                  Globe Pvt Ltd. (&quot;Company&quot; / &quot;StarClinch&quot;). This Privacy Policy outlines how we collect,
                  use, and protect your personal information across both platforms.
                </p>
              </div>
            )}

          <div className="mt-6 space-y-3">
            {privacyItems.map((item) => (
              <button
                key={item.n}
                type="button"
                onClick={() =>
                  setExpandedPrivacyCardId((prev) => (prev === item.n ? null : item.n))
                }
                aria-expanded={expandedPrivacyCardId === item.n}
                className="w-full rounded-2xl border border-[#F33363]/20 bg-[#0B0B0E] px-4 py-4 text-left"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="mt-0.5 inline-flex h-8 w-8 aspect-square items-center justify-center rounded-full border border-[#F33363]/30 bg-[#4B0017]/50">
                      <span className="text-sm font-semibold text-[#F33363]">{item.n}</span>
                    </div>

                    <div className="text-left">
                      <div className="text-sm font-semibold text-white">{item.title}</div>
                    </div>
                  </div>

                  <ChevronDown
                    className={`h-5 w-5 text-white/70 transition-transform ${
                      expandedPrivacyCardId === item.n ? 'rotate-180' : ''
                    }`}
                  />
                </div>

                {expandedPrivacyCardId === item.n && <div className="mt-3">{item.expanded}</div>}
              </button>
            ))}
          </div>
          </div>
        </Sheet>

        <EnableExpressBookingDrawer
          open={enableDrawerOpen}
          onOpenChange={setEnableDrawerOpen}
          onSaveSuccess={refreshRejectedLocationFlag}
          onSuccessNavigate={() => navigate('/express-booking')}
        />
      </div>
    </div>
  );
};
